// @ts-nocheck
/*
 * Cloud Telephony nav filter — strict v1 parity.
 *
 * v1's canView(mod):
 *   return !!mod?.[moduleName]?.view_permission;
 * (both "hideForSuperadmin" and "isSuperAdmin bypasses" branches are
 * commented out in the live code — so no role magically bypasses ACL).
 *
 * Rules — evaluated in order for each item:
 *
 *   1. `roles`   — the user's role must be in this array. If not, hide.
 *   2. `aclModule` — if set, the user's `moduleOperations[aclModule]
 *      .view_permission` must be truthy. If not, hide.
 *   3. `bypassAclForRoles` — narrow exception: certain items in v1 are
 *      gated as `isSuperAdmin || canView(mod)` (MOH Management, Flash
 *      Message, Session Replay) or `isNotificationAdmin || canView`
 *      (Notifications). Users whose role is in this array skip the
 *      aclModule check on this specific item.
 *
 * User data is read directly from Storage.getItem('user') — same source
 * every ported widget already uses; no separate auth store is needed.
 */
import { Storage } from '@/utilities/setting';
import { isGroup, type NavItem, type NavLeaf } from './nav-config';

interface CurrentUser {
  role: string | null;
  moduleOperations: Record<string, any>;
  isOwnCompany: boolean;
}

export function getCurrentUser(): CurrentUser {
  try {
    const raw = Storage.getItem('user');
    if (!raw) return { role: null, moduleOperations: {}, isOwnCompany: false };
    const user = JSON.parse(raw);
    const role = user?.role || null;
    let mod: Record<string, any> = {};
    try {
      const ops = user?.acl_settings?.module_operations;
      if (ops) {
        mod = (typeof ops === 'string' ? JSON.parse(ops) : ops) || {};
      } else if (user?.moduleOperations) {
        mod = user.moduleOperations;
      }
    } catch {
      mod = {};
    }
    return {
      role,
      moduleOperations: mod,
      isOwnCompany: user?.assignedCompany?.is_own_company === true,
    };
  } catch {
    return { role: null, moduleOperations: {}, isOwnCompany: false };
  }
}

function canView(item: NavItem | NavLeaf, user: CurrentUser): boolean {
  if (!user.role) return false;
  if (!item.roles || !item.roles.includes(user.role)) return false;
  if (item.hideForOwnCompany && user.isOwnCompany) return false;

  if (!item.aclModule) return true;

  // Item-specific bypass (v1's `isSuperAdmin || canView(...)` pattern).
  if (
    Array.isArray(item.bypassAclForRoles) &&
    item.bypassAclForRoles.includes(user.role)
  ) {
    return true;
  }

  // Strict canView — matches v1 exactly. No role gets a free pass by
  // default; the aclModule must be granted view_permission.
  const modAcl = user.moduleOperations?.[item.aclModule];
  const moduleAllowed = !!(modAcl && modAcl.view_permission);
  if (moduleAllowed) {
    if (Array.isArray(item.requireAclOps)) {
      return item.requireAclOps.every((op) => !!modAcl[op]);
    }
    return true;
  }

  return false;
}

export function filterNav(items: NavItem[]): NavItem[] {
  const user = getCurrentUser();
  if (!user.role) return [];

  return items
    .map((item) => {
      if (!canView(item, user)) return null;
      if (isGroup(item)) {
        const kids = item.children.filter((c) => canView(c, user));
        if (kids.length === 0) return null;
        return { ...item, children: kids };
      }
      return item;
    })
    .filter((x): x is NavItem => x !== null);
}

function normalizeSearch(value: string) {
  return value.trim().toLowerCase();
}

function labelMatches(label: string, query: string) {
  if (!query) return true;
  return label.toLowerCase().includes(query);
}

/** Keep a module if its own name or any nested child name matches. */
export function filterNavByQuery(items: NavItem[], rawQuery: string): NavItem[] {
  const query = normalizeSearch(rawQuery);
  if (!query) return items;

  return items
    .map((item) => {
      if (isGroup(item)) {
        const parentHit = labelMatches(item.label, query);
        const kids = parentHit
          ? item.children
          : item.children.filter((child) => labelMatches(child.label, query));
        if (!parentHit && kids.length === 0) return null;
        return { ...item, children: kids };
      }
      return labelMatches(item.label, query) ? item : null;
    })
    .filter((item): item is NavItem => item !== null);
}
